'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import styles from './NavBar.module.css';

export default function NavBar() {
  const { user, isLoading } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAgentsOpen, setIsAgentsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  /* Close mobile menu when clicking outside */
  useEffect(() => {
    if (!isMenuOpen) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isMenuOpen]);

  /* Prevent body scroll when mobile menu is open */
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const closeMenu = () => { setIsMenuOpen(false); setIsAgentsOpen(false); };

  return (
    <nav className={styles.novaNav} ref={menuRef}>
      <Link href="/" className={styles.navLogo} onClick={closeMenu}>
        <Image
          src="/NovaMind-AI.png"
          alt="NovaMind AI"
          width={60}
          height={20}
          className={styles.navLogoImg}
        />
        NovaMind AI
      </Link>

      {/* ── Desktop Nav Links ── */}
      <ul className={styles.navLinks}>
        <li className={styles.navDropdownWrap}>
          <span className={styles.navDropdownToggle}>
            AI Agents <span className={styles.navCaret}>▾</span>
          </span>
          <div className={styles.navDropdown}>
            <a href="/solutions/medical">Medical Agent</a>
            <a href="/solutions/dental">Dental Agent</a>
            <a href="/solutions/restaurant">Restaurant Agent</a>
            <a href="/solutions/general">General Agent</a>
            <a href="/solutions/custom">Custom Agent</a>
          </div>
        </li>
        <li><a href="/#features">Features</a></li>
        <li><a href="/#pricing">Pricing</a></li>
        <li><a href="/#faq">FAQ</a></li>
        <li><a href="/partners">Partners</a></li>
        <li className={styles.navDropdownWrap}>
          <span className={styles.navDropdownToggle}>
            Company <span className={styles.navCaret}>▾</span>
          </span>
          <div className={styles.navDropdown}>
            <a href="/about">About</a>
            <a href="/blog">Blog</a>
          </div>
        </li>
      </ul>

      {/* ── Desktop Right Buttons ── */}
      <div className={styles.navRight}>
        {isLoading ? (
          <div className={styles.navLoadingPlaceholder} />
        ) : user ? (
          <Link href="/dashboard" className={styles.btnNav}>Dashboard</Link>
        ) : (
          <>
            <Link href="/contact"  className={styles.navLogin}>Contact</Link>
            <Link href="/login"    className={styles.navLogin}>Login</Link>
            <Link href="/register" className={styles.btnNav}>Start Free</Link>
          </>
        )}
      </div>

      {/* ── Hamburger Button (mobile only) ── */}
      <button
        className={styles.hamburger}
        onClick={() => setIsMenuOpen(prev => !prev)}
        aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isMenuOpen}
      >
        <span className={`${styles.hamburgerBar} ${isMenuOpen ? styles.hamburgerBarTop : ''}`} />
        <span className={`${styles.hamburgerBar} ${isMenuOpen ? styles.hamburgerBarMid : ''}`} />
        <span className={`${styles.hamburgerBar} ${isMenuOpen ? styles.hamburgerBarBot : ''}`} />
      </button>

      {/* ── Mobile Menu Panel ── */}
      {isMenuOpen && (
        <div className={styles.mobileMenu}>

          {/* AI Agents accordion */}
          <button
            className={styles.mobileMenuAccordion}
            onClick={() => setIsAgentsOpen(prev => !prev)}
            aria-expanded={isAgentsOpen}
          >
            AI Agents
            <span className={`${styles.mobileMenuCaret} ${isAgentsOpen ? styles.mobileMenuCaretOpen : ''}`}>▾</span>
          </button>
          {isAgentsOpen && (
            <div className={styles.mobileMenuSubLinks}>
              <a href="/solutions/medical"     className={styles.mobileMenuSubLink} onClick={closeMenu}>Medical Agent</a>
              <a href="/solutions/dental"      className={styles.mobileMenuSubLink} onClick={closeMenu}>Dental Agent</a>
              <a href="/solutions/restaurant" className={styles.mobileMenuSubLink} onClick={closeMenu}>Restaurant Agent</a>
              <a href="/solutions/general"     className={styles.mobileMenuSubLink} onClick={closeMenu}>General Agent</a>
              <a href="/solutions/custom"      className={styles.mobileMenuSubLink} onClick={closeMenu}>Custom Agent</a>
            </div>
          )}

          <div className={styles.mobileMenuDivider} />

          {/* Main links */}
          <a href="/#features" className={styles.mobileMenuLink} onClick={closeMenu}>Features</a>
          <a href="/#pricing"  className={styles.mobileMenuLink} onClick={closeMenu}>Pricing</a>
          <a href="/#faq"      className={styles.mobileMenuLink} onClick={closeMenu}>FAQ</a>
          <a href="/partners"  className={styles.mobileMenuLink} onClick={closeMenu}>Partners</a>
          <a href="/about"     className={styles.mobileMenuLink} onClick={closeMenu}>About</a>
          <a href="/blog"      className={styles.mobileMenuLink} onClick={closeMenu}>Blog</a>
          <a href="/contact"   className={styles.mobileMenuLink} onClick={closeMenu}>Contact</a>

          <div className={styles.mobileMenuDivider} />

          {/* Auth */}
          {isLoading ? (
            <div className={styles.navLoadingPlaceholder} style={{ margin: '8px 20px' }} />
          ) : user ? (
            <Link href="/dashboard" className={styles.mobileMenuBtnPrimary} onClick={closeMenu}>Dashboard</Link>
          ) : (
            <>
              <Link href="/login"    className={styles.mobileMenuBtnGhost}   onClick={closeMenu}>Login</Link>
              <Link href="/register" className={styles.mobileMenuBtnPrimary} onClick={closeMenu}>Start Free</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
